import React, { useEffect, useState } from "react";
import { Box, Spinner, Heading, useTheme } from "@chakra-ui/react";
import axios from "axios";
import * as am5 from "@amcharts/amcharts5";
import * as am5percent from "@amcharts/amcharts5/percent";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

const colorPalette = [
  "#58b6c0",
  "#58c087",
  "rgba(88, 182, 192, 0.6)",
  "rgba(88, 192, 135, 0.6)",
  "rgba(88, 182, 192, 0.4)",
  "rgba(88, 192, 135, 0.4)",
  "rgba(88, 182, 192, 0.25)",
  "rgba(88, 192, 135, 0.25)",
];

const Chart1 = ({ supplier, retailer }) => {
  const theme = useTheme();
  const [chartDiv, setChartDiv] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let root;

    const initChart = async () => {
      if (chartDiv) {
        setLoading(true);
        try {
          const res = await axios.post(
            `${process.env.REACT_APP_API_URL}/rep_sales/chart1`,
            {
              supplier,
              retailer,
              userid: localStorage.getItem("userid"),
            }
          );

          const data = res.data.data.map((item) => ({
            category: item.site,
            value: Math.round(item.value),
          }));

          root = am5.Root.new(chartDiv);
          root.setThemes([am5themes_Animated.new(root)]);

          const chart = root.container.children.push(
            am5percent.PieChart.new(root, {
              radius: am5.percent(80),
              innerRadius: am5.percent(50),
              layout: root.horizontalLayout,
            })
          );

          const series = chart.series.push(
            am5percent.PieSeries.new(root, {
              name: "Series",
              valueField: "value",
              categoryField: "category",
              colors: am5.ColorSet.new(root, {
                colors: colorPalette.map((c) => am5.color(c)),
              }),
            })
          );

          series.data.setAll(data);

          // Slice styling — white borders, no gradient
          series.slices.template.setAll({
            strokeOpacity: 1,
            stroke: am5.color("#ffffff"),
            strokeWidth: 2,
          });

          // Labels — hide by default, show on hover to avoid overlap
          series.labels.template.setAll({
            visible: false,
            fontSize: 11,
            fill: am5.color("#6b7280"),
          });
          series.slices.template.states.create("hover", {});
          series.labels.template.states.create("hover", { visible: true });

          series.labels.template.adapters.add("text", function (text, target) {
            const dataItem = target.dataItem;
            if (dataItem) {
              const category = dataItem.get("category");
              const value = dataItem.get("value");
              if (value >= 1000000) return `${category}: R${(value / 1000000).toFixed(1)}M`;
              if (value >= 1000) return `${category}: R${(value / 1000).toFixed(0)}K`;
              return `${category}: R${root.numberFormatter.format(value, "#,###")}`;
            }
            return text;
          });

          // Tooltips
          const tooltip = am5.Tooltip.new(root, {
            getFillFromSprite: false,
            labelText: "{category}: R{value} ({valuePercentTotal}%)",
          });
          tooltip.get("background").setAll({
            fill: am5.color("#ffffff"),
            fillOpacity: 0.95,
            stroke: am5.color("#e5e7eb"),
            strokeWidth: 1,
            shadowColor: am5.color("#000000"),
            shadowBlur: 8,
            shadowOffsetX: 0,
            shadowOffsetY: 2,
            shadowOpacity: 0.08,
            cornerRadius: 6,
          });
          tooltip.label.setAll({
            fill: am5.color("#374151"),
            fontSize: 12,
          });
          series.slices.template.set("tooltip", tooltip);

          series.slices.template.adapters.add("tooltipText", function (text, target) {
            const dataItem = target.dataItem;
            if (dataItem) {
              const category = dataItem.get("category");
              const value = dataItem.get("value");
              const percent = dataItem.get("valuePercentTotal");
              const formattedValue = root.numberFormatter.format(value, "#,###");
              const formattedPercent = root.numberFormatter.format(percent, "0.0");
              return `${category}: R${formattedValue} (${formattedPercent}%)`;
            }
            return text;
          });

          // Legend
          const legend = chart.children.push(
            am5.Legend.new(root, {
              centerY: am5.percent(50),
              y: am5.percent(50),
              layout: root.verticalLayout,
            })
          );

          legend.itemContainers.template.setAll({
            paddingTop: 2,
            paddingBottom: 2,
            marginTop: 0,
            marginBottom: 0,
          });

          legend.labels.template.setAll({
            text: "{category}",
            fontSize: "12px",
            fill: am5.color("#6b7280"),
          });

          legend.valueLabels.template.setAll({
            text: "",
            textAlign: "right",
            fontSize: "12px",
            fill: am5.color("#9ca3af"),
          });

          legend.valueLabels.template.adapters.add("text", (text, target) => {
            const dataItem = target.dataItem;
            if (dataItem) {
              const percent = dataItem.get("valuePercentTotal");
              const formattedPercent = root.numberFormatter.format(percent, "0.0");
              return ` (${formattedPercent}%)`;
            }
            return text;
          });

          legend.markerRectangles.template.setAll({
            width: 10,
            height: 10,
            marginRight: 5,
            cornerRadiusTL: 2,
            cornerRadiusTR: 2,
            cornerRadiusBL: 2,
            cornerRadiusBR: 2,
          });

          legend.data.setAll(series.dataItems);

          series.appear(1000, 100);
        } catch (error) {
          console.error("Error fetching data", error);
        } finally {
          setLoading(false);
        }
      }
    };

    initChart();

    return () => {
      if (root) {
        root.dispose();
      }
    };
  }, [chartDiv, supplier, retailer]);

  return (
    <Box
      height="100%"
      display="flex"
      flexDirection="column"
      position="relative"
    >
      <Heading size="xs" color="gray.600" mb={2} fontWeight="600">
        Sales by Site Profile
      </Heading>
      <Box flex="1" position="relative">
        <div
          ref={(div) => setChartDiv(div)}
          style={{ width: "100%", height: "100%", overflow: "hidden" }}
        ></div>
        {loading && (
          <Spinner
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
          />
        )}
      </Box>
    </Box>
  );
};

export default Chart1;
